import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment-timezone';

import PatientList from './PatientList';
import { RPM_PATIENT_STATUS } from '../../../constants';
import { Tab, Tabs } from '../../../components/Tabs';
import Strings from '../../../Strings';
import { actions } from './redux/actions';
import { actions as cohortActions } from './Cohorts/redux/actions';
import { getFiltersForRequest, getFilterValueFromUrl, getRelevantFilters, urlInjected } from '../../../components/AdvancedFilters/helpers';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { FacilityFilter, ProviderFilter } from './Cohorts/Cohorts';
import { TextInput } from '../../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import { hydrateTableRequestFromUrl, updateUrlParamsPreservingHistoryState, useGlobalPageSize } from '../../../utils/tableUtils';
import { ORG_FEATURES, orgFeatureEnabled } from '../../../utils/userPermissions';
import { hasAccessAlignmentInFlight, hasAccessEligibilityInFlight } from '../../../utils/cmsPrograms';

export const PatientListMode = {
  list: 'list',
  tabsByStatus: 'tabsByStatus',
};

export const PatientViewMode = {
  patients: 'patients',
  enrollments: 'enrollments',
};

const PATIENT_VIEW_MODE_STORAGE_KEY = 'patients.viewMode';

export const getStoredPatientViewMode = userId => {
  if (userId === undefined || userId === null) {
    return null;
  }
  try {
    const storedViewMode = window.localStorage.getItem(`${PATIENT_VIEW_MODE_STORAGE_KEY}.${userId}`);
    return Object.values(PatientViewMode).includes(storedViewMode) ? storedViewMode : null;
  } catch (error) {
    return null;
  }
};

export const storePatientViewMode = (userId, viewMode) => {
  if (userId === undefined || userId === null) {
    return;
  }
  try {
    window.localStorage.setItem(`${PATIENT_VIEW_MODE_STORAGE_KEY}.${userId}`, viewMode);
  } catch (error) {
    // Keep the in-memory selection if browser storage is unavailable.
  }
};

export function PatientViewSwitch({ value, onChange }) {
  const patientViewSelected = value === PatientViewMode.patients;

  return (
    <div className="patient-view-switch">
      <span className={`patient-view-switch-option ${!patientViewSelected ? 'active' : ''}`}>
        {Strings.enrollmentView}
      </span>
      <label className="patient-view-switch-control">
        <input
          type="checkbox"
          role="switch"
          checked={patientViewSelected}
          aria-label={`${Strings.enrollmentView} / ${Strings.patientView}`}
          data-testid="patient-view-switch"
          onChange={event => onChange(
            event.target.checked ? PatientViewMode.patients : PatientViewMode.enrollments,
          )}
        />
        <span className="patient-view-switch-track" aria-hidden="true" />
      </label>
      <span className={`patient-view-switch-option ${patientViewSelected ? 'active' : ''}`}>
        {Strings.patientView}
      </span>
    </div>
  );
}

PatientViewSwitch.propTypes = {
  value: PropTypes.oneOf(Object.values(PatientViewMode)).isRequired,
  onChange: PropTypes.func.isRequired,
};

function PatientsTabs(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const hasFiltersInUrl = urlInjected(window.location.search);
  const statusFromUrl = urlParams.get('status');
  const offsetFromUrl = urlParams.get('offset');
  const searchFromUrl = urlParams.get('search');
  const globalPageSize = useGlobalPageSize();
  const { 
    cohortId, 
    refreshTimestamp, 
    filters, 
    orgIds, 
    orgs,
    getPatients, 
    displayMode = PatientListMode.tabsByStatus, 
    getPatientStatusCounts,
    getPatientProviderOptions,
    getFacilities,
    clearData,
    isFiltersInitialized,
    viewMode: requestedViewMode = PatientViewMode.enrollments,
  } = props;
  const initialViewMode = displayMode === PatientListMode.tabsByStatus
    ? requestedViewMode
    : PatientViewMode.enrollments;
  const [facilities, setFacilities] = React.useState(null);
  const [providers, setProviders] = React.useState([]);
  const [viewMode, setViewMode] = React.useState(initialViewMode);
  const [status, setStatus] = React.useState(displayMode === PatientListMode.tabsByStatus ? (statusFromUrl || RPM_PATIENT_STATUS.activated) : undefined);
  const [searchText, setSearchText] = React.useState(searchFromUrl || '');

  const defaultPatientStatusCount = React.useMemo(() => 
    Object.keys(RPM_PATIENT_STATUS).map(statusKey => ({status: statusKey, count: '?'}))
  , []);
  
  const [patientsCount, setPatientsCount] = React.useState(defaultPatientStatusCount);
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
  const hasSeenHydratedFilters = React.useRef(false);
  const searchTime = React.useRef(null);
  const pageRequest = React.useRef({
    status: initialViewMode === PatientViewMode.patients ? undefined : status,
    offset: offsetFromUrl ? parseInt(offsetFromUrl, 10) : 0,
    limit: globalPageSize,
    search: searchFromUrl || '',
    orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
    lastReadings: true,
    missedToday: true,
    alertDetails: initialViewMode === PatientViewMode.enrollments && status === RPM_PATIENT_STATUS.activated ? true : undefined,
    view: initialViewMode === PatientViewMode.patients ? PatientViewMode.patients : undefined,
  });

  const saveSearchToUrlParams = () => {
    updateUrlParamsPreservingHistoryState({
      search: pageRequest.current.search || null,
    });
  };

  const load = React.useCallback(() => {
    if (cohortId) pageRequest.current.cohortId = cohortId;
    else delete pageRequest.current.cohortId;
    saveSearchToUrlParams();
    getPatients(pageRequest.current);
  }, [cohortId, getPatients]);

  React.useEffect(() => {
    if (props.pagination?.offset !== pageRequest.current.offset) {
      return;
    }
    if (props.patients && props.pagination) {
      if (props.patients.length === 0 && props.pagination.totalRecords > 0) {
        // If no patients are returned but there are totalRecords, it means the current offset is out of range
        // Reset to previous page
        pageRequest.current.offset = Math.max(0, props.pagination.offset - globalPageSize);
        load();
      }
    }
  }, [props.patients, props.pagination]);

  const getPatientsCount = React.useCallback(() => {
    if (displayMode !== PatientListMode.tabsByStatus || viewMode !== PatientViewMode.enrollments) {
      return;
    }

    setPatientsCount(defaultPatientStatusCount);
    const request = {
      ...pageRequest.current,
      offset: 0,
      orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
    };
    delete request.lastReadings;
    delete request.missedToday;
    delete request.alertDetails;
    delete request.status;

    getPatientStatusCounts(request).then(resp => {
      const counts = resp.response?.counts;
      if (counts) {
        setPatientsCount(Object.keys(RPM_PATIENT_STATUS).map(statusKey => ({
          status: statusKey,
          count: counts[statusKey] ?? 0,
        })));
      }
    });
  }, [displayMode, viewMode, getPatientStatusCounts, defaultPatientStatusCount, orgIds]);

  React.useEffect(() => {
    getFacilities().then(resp => {
      setFacilities(resp.response?.data || []);
    });
  }, [getFacilities]);

  const loadProviderOptions = React.useCallback((extraRequest = {}) => {
    const request = {
      orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
      status: viewMode === PatientViewMode.enrollments ? status : undefined,
      ...(cohortId ? { cohortId } : {}),
      ...extraRequest,
    };
    return getPatientProviderOptions(request).then(resp => {
      setProviders(resp.response?.data || []);
    });
  }, [cohortId, getPatientProviderOptions, orgIds, status, viewMode]);

  React.useEffect(() => {
    loadProviderOptions();
  }, [loadProviderOptions]);

  const refreshData = React.useCallback(() => {
    load();
    getPatientsCount();
  }, [load, getPatientsCount]);

  const hasPendingAccessRequest = React.useMemo(
    () => props.patients?.some(
      patient => hasAccessEligibilityInFlight(patient) || hasAccessAlignmentInFlight(patient),
    ) === true,
    [props.patients],
  );

  React.useEffect(() => {
    if (!hasPendingAccessRequest) {
      return undefined;
    }
    const intervalId = window.setInterval(refreshData, 5000);
    return () => window.clearInterval(intervalId);
  }, [hasPendingAccessRequest, refreshData]);

  const getPageRequestFromFilters = (filterState, targetViewMode = viewMode) => {
    const ret = {};
    ret.filterBy = [];

    const programs = getRelevantFilters(filterState.filter(f => f.group === 'programCheckbox'));
    if (programs?.length > 0) {
      ret.filterBy.push(`program:${programs.map(f => f.value).join(',')}`);
    }

    const facilityFilters = getRelevantFilters(filterState.filter(f => f.group === 'facilityId'));
    if (facilityFilters?.length > 0) {
      ret.facilityId = facilityFilters.map(f => f.value).join(',');
    }

    if (targetViewMode === PatientViewMode.patients) {
      const enrollmentStatuses = getRelevantFilters(filterState.filter(f => f.group === 'patientEnrollmentStatus'));
      if (enrollmentStatuses?.length > 0) {
        ret.filterBy.push(`status:${enrollmentStatuses.map(f => f.value).join(',')}`);
      }
      if (ret.filterBy.length === 0) {
        delete ret.filterBy;
      }
      return ret;
    }

    const noReadingsSince = getRelevantFilters(filterState.filter(f => f.group === 'NoReadingsSince'));
    if (noReadingsSince?.length > 0) {
      ret.noReadingsSince = moment()
        .subtract(noReadingsSince[noReadingsSince.length - 1].state, 'days')
        .startOf('day')
        .format();
    }

    const newNotesSince = getRelevantFilters(filterState.filter(f => f.group === 'NewNotesSince'));
    if (newNotesSince?.length > 0) {
      ret.noteStartTimestamp = moment()
        .subtract(newNotesSince[newNotesSince.length - 1].state, 'days')
        .startOf('day')
        .format();
    }

    const outreachStatus = getFiltersForRequest(filterState.filter(f => f.group === 'consentForProvider'));
    if (outreachStatus?.length > 0) {
      ret.filterBy.push(outreachStatus);
    }

    const outreachStatusExtended = getFiltersForRequest(filterState.filter(f => f.group === 'outreachStatus'));
    if (outreachStatusExtended?.length > 0) {
      ret.filterBy.push(outreachStatusExtended);
    }

    const shipmentStatus = getRelevantFilters(filterState.filter(f => f.group === 'shipmentStatus'));
    if (shipmentStatus?.length > 0) {
      ret.filterBy.push(`shipmentStatus:${shipmentStatus.map(f => f.value).join(',')}`);
    }

    const providerFilters = getRelevantFilters(filterState.filter(f => f.group === 'provider'));
    if (providerFilters?.length > 0) {
      ret.filterBy.push(`provider:${providerFilters.map(f => f.value).join(',')}`);
    }

    if (getRelevantFilters(filterState).find(f => f.group === 'missingMedicationSchedule')?.value === 'true') {
      ret.missingMedicationSchedule = true;
    }

    const billingAlerts = getRelevantFilters(filterState.filter(f => f.group === 'billingAlerts'));
    ret.alert = billingAlerts?.length > 0 ? billingAlerts.map(f => f.value) : undefined;

    if (ret.filterBy.length === 0) {
      delete ret.filterBy;
    }
    return ret;
  };

  const deleteUnusedPageRequestFields = (pageRequestFromFilters) => {
    const fieldsToCheck = ['facilityId', 'noReadingsSince', 'noteStartTimestamp', 'filterBy', 'alert', 'missingMedicationSchedule'];
    fieldsToCheck.forEach(field => {
      if (!pageRequestFromFilters[field] && pageRequestFromFilters[field] !== '') {
        delete pageRequest.current[field];
      }
    });
  };

  const onViewModeChange = nextViewMode => {
    if (nextViewMode === viewMode) {
      return;
    }

    const filterPageRequest = getPageRequestFromFilters(filters, nextViewMode);
    pageRequest.current = { ...pageRequest.current, ...filterPageRequest, offset: 0 };
    deleteUnusedPageRequestFields(filterPageRequest);

    const urlUpdates = {
      view: nextViewMode,
      status: nextViewMode === PatientViewMode.patients ? undefined : status,
    };

    if (nextViewMode === PatientViewMode.patients) {
      pageRequest.current.view = PatientViewMode.patients;
      delete pageRequest.current.status;
      delete pageRequest.current.alertDetails;
    } else {
      delete pageRequest.current.view;
      pageRequest.current.status = status;
      if (status === RPM_PATIENT_STATUS.activated) pageRequest.current.alertDetails = true;
      else delete pageRequest.current.alertDetails;
    }
    updateUrlParamsPreservingHistoryState(urlUpdates, ['offset']);
    setViewMode(nextViewMode);
  };

  React.useEffect(() => {
    if (displayMode === PatientListMode.tabsByStatus && requestedViewMode !== viewMode) {
      onViewModeChange(requestedViewMode);
    }
  }, [displayMode, requestedViewMode, viewMode]);

  const onFiltersChange = React.useMemo(() => _.debounce(filterState => {
    pageRequest.current.offset = 0;
    const filterPageRequest = getPageRequestFromFilters(filterState);
    pageRequest.current = { ...pageRequest.current, ...filterPageRequest };
    deleteUnusedPageRequestFields(filterPageRequest);

    refreshData();
    const providerFilters = getRelevantFilters(filterState.filter(f => f.group === 'provider'));
    if (providerFilters.length === 0) {
      loadProviderOptions({
        ...filterPageRequest,
        filterBy: filterPageRequest.filterBy?.filter(filter => !filter.startsWith('provider:')),
      });
    }
  }, 1000), [loadProviderOptions, refreshData, viewMode]);

  React.useEffect(() => {
    return () => {
      onFiltersChange.cancel();
    };
  }, [onFiltersChange]);

  React.useEffect(() => {
    if (viewMode === PatientViewMode.enrollments && status && status !== pageRequest.current.status) {
      pageRequest.current.status = status;
      if (status === RPM_PATIENT_STATUS.activated) pageRequest.current.alertDetails = true;
      else delete pageRequest.current.alertDetails;
      updateUrlParamsPreservingHistoryState({ status }, ['offset']);
      load();
    }
  }, [status, viewMode]);

  React.useEffect(() => {
    const filtersFromUrl = getFilterValueFromUrl();
    if (filtersFromUrl?.length) {
      const filterPageRequest = getPageRequestFromFilters(filtersFromUrl);
      pageRequest.current = { ...pageRequest.current, ...filterPageRequest };
      deleteUnusedPageRequestFields(filterPageRequest);
    }

    hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
    pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;
    load();
    getPatientsCount();
    return () => {
      clearData();
    };
  }, [cohortId, orgIds, refreshTimestamp, load, getPatientsCount, globalPageSize]);

  // Memoize filters to prevent unnecessary re-renders caused by new filter array references
  const memoizedFilters = React.useMemo(() => filters, [JSON.stringify(filters)]);

  React.useEffect(() => {
    if (!isFiltersInitialized) {
      return;
    }

    // During initial URL filter injection, Redux filters can transiently be [] before hydration.
    // Ignore that transient empty state so URL-derived request params are not cleared.
    if (hasFiltersInUrl && !hasSeenHydratedFilters.current && memoizedFilters.length === 0) {
      return;
    }

    if (memoizedFilters.length > 0) {
      hasSeenHydratedFilters.current = true;
    }

    const pageRequestFromMemorizedFilters = getPageRequestFromFilters(memoizedFilters);

    if (
      pageRequest.current.facilityId !== pageRequestFromMemorizedFilters.facilityId ||
      pageRequest.current.noReadingsSince !== pageRequestFromMemorizedFilters.noReadingsSince ||
      pageRequest.current.noteStartTimestamp !== pageRequestFromMemorizedFilters.noteStartTimestamp ||
      pageRequest.current.missingMedicationSchedule !== pageRequestFromMemorizedFilters.missingMedicationSchedule ||
      !_.isEqual(pageRequest.current.filterBy, pageRequestFromMemorizedFilters.filterBy) ||
      !_.isEqual(pageRequest.current.alert, pageRequestFromMemorizedFilters.alert)
    ) {
      onFiltersChange(memoizedFilters);
    }
  }, [isFiltersInitialized, memoizedFilters, onFiltersChange]);

  const onSortingChange = React.useCallback(sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.offset = 0;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    load();
  }, [load]);

  const onOffsetChange = React.useCallback((offset, limit) => {
    if (isNaN(offset)) return;
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    load();
  }, [load]);

  const onPageSizeChange = React.useCallback(limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    load();
  }, [load]);

  const onSearchQueryChange = React.useCallback(query => {
    setSearchText(query);
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    clearTimeout(searchTime.current);
    searchTime.current = setTimeout(() => {
      refreshData();
    }, 1000);
  }, [refreshData]);

  const getTabRightContent = () => {
    return (
      <>
        <TextInput placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} value={searchText} />
        {(facilities || viewMode === PatientViewMode.patients) && (
          <AdvancedFilters>
            {viewMode === PatientViewMode.patients && <Filter definition={getFiltersDefinition().patientProgramCheckbox} />}
            {viewMode === PatientViewMode.patients && <Filter definition={getFiltersDefinition().patientEnrollmentStatus} />}
            {viewMode === PatientViewMode.patients && facilities?.length > 1 && <Filter definition={FacilityFilter(facilities, orgs, Strings.facility).facilityId} />}
            {viewMode === PatientViewMode.enrollments
              && status === RPM_PATIENT_STATUS.onboarded
              && !extendedOutreachStatusEnabled
              && <Filter definition={getFiltersDefinition().consentForProvider} />}
            {viewMode === PatientViewMode.enrollments && extendedOutreachStatusEnabled &&
            (status === RPM_PATIENT_STATUS.verified || status === RPM_PATIENT_STATUS.notEnrolled) && (
              <Filter definition={getFiltersDefinition().outreachStatus} />
            )}
            {viewMode === PatientViewMode.enrollments
              && (status === RPM_PATIENT_STATUS.enrolled || status === RPM_PATIENT_STATUS.activated)
              && <Filter definition={getFiltersDefinition().programCheckbox} />}
            {viewMode === PatientViewMode.enrollments && facilities?.length > 1 && <Filter definition={FacilityFilter(facilities, orgs, Strings.facility).facilityId} />}
            {viewMode === PatientViewMode.enrollments && providers?.length > 0 && <Filter definition={ProviderFilter(providers).provider} />}
            {viewMode === PatientViewMode.enrollments && <Filter definition={getFiltersDefinition().NoReadingsSince} />}
            {viewMode === PatientViewMode.enrollments && <Filter definition={getFiltersDefinition().NewNotesSince} />}
            {viewMode === PatientViewMode.enrollments && <Filter definition={getFiltersDefinition().shipmentStatus} />}
            {viewMode === PatientViewMode.enrollments
              && (status === RPM_PATIENT_STATUS.enrolled || status === RPM_PATIENT_STATUS.activated)
              && <Filter definition={getFiltersDefinition().missingMedicationSchedule} />}
            {viewMode === PatientViewMode.enrollments
              && (status === RPM_PATIENT_STATUS.enrolled || status === RPM_PATIENT_STATUS.activated)
              && <Filter definition={getFiltersDefinition().billingAlerts} />}
          </AdvancedFilters>
        )}
      </>
    );
  };

  const tabArray = Object.keys(RPM_PATIENT_STATUS).map(s => (
    <Tab label={`${Strings.capPatient.patientStatus[s]} (${patientsCount.find(p => p.status === s)?.count})`} key={s}>
      <PatientList
        status={s}
        facilities={facilities}
        onSortingChange={onSortingChange}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        forceRefresh={refreshData}
      />
    </Tab>
  ));

  return (
    <div className="patients-tabs-container">
      {facilities
        ? <AdvancedFiltersBar customFilters={{ ...FacilityFilter(facilities, orgs, Strings.facility), ...ProviderFilter(providers) }} />
        : null}
      {displayMode === PatientListMode.tabsByStatus && viewMode === PatientViewMode.enrollments && (
        <Tabs
          defaultIndex={tabArray.findIndex(t => t.key === status)}
          onChange={i => { setStatus(Object.keys(RPM_PATIENT_STATUS)[i]); pageRequest.current.offset = 0}}
          rightContent={getTabRightContent()}
        >
          {tabArray}
        </Tabs>
      )}
      {displayMode === PatientListMode.tabsByStatus && viewMode === PatientViewMode.patients && (
        <>
          <div className="tab-buttons-row patient-view-toolbar">
            <div className="tab-buttons-right">{getTabRightContent()}</div>
          </div>
          <PatientList
            patientView
            onSortingChange={onSortingChange}
            onOffsetChange={onOffsetChange}
            onPageSizeChange={onPageSizeChange}
            forceRefresh={refreshData}
          />
        </>
      )}
      {displayMode === PatientListMode.list && (
        <PatientList
          onSortingChange={onSortingChange}
          onOffsetChange={onOffsetChange}
          onPageSizeChange={onPageSizeChange}
          forceRefresh={refreshData}
        />
      )}
    </div>
  );
}

PatientsTabs.propTypes = {
  getPatients: PropTypes.func,
  isLoading: PropTypes.any,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
  }),
  cohortId: PropTypes.string,
  refreshTimestamp: PropTypes.number,
  patients: PropTypes.array,
  filters: PropTypes.array,
  clearData: PropTypes.func,
  getPatientStatusCounts: PropTypes.func,
  getPatientProviderOptions: PropTypes.func,
  getFacilities: PropTypes.func,
  displayMode: PropTypes.string,
  orgIds: PropTypes.array,
  orgs: PropTypes.array,
  isFiltersInitialized: PropTypes.bool,
  viewMode: PropTypes.oneOf(Object.values(PatientViewMode)),
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    isLoading: patients && patients?.isLoading,
    pagination: patients && patients?.data?.pagination,
    patients: patients && patients?.data?.data,
    filters: state.entities.advancedFilters.filters.items,
    isFiltersInitialized: state.entities.advancedFilters.filters.isInitalized,
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToProps = {
  getPatients: actions.getPatients,
  getPatientStatusCounts: actions.getPatientStatusCounts,
  getPatientProviderOptions: actions.getPatientProviderOptions,
  clearData: actions.clearPatients,
  getFacilities: cohortActions.getFacilities,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientsTabs);
